import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Warrant } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Warrants() {
  const { persona } = useAuth();
  const { fy } = useRefData();
  const [showDraft, setShowDraft] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQ = useApiQuery<{ items: Warrant[] }>(fy ? `/api/v1/warrants/?fiscalYearId=${fy.id}&limit=100` : null);
  const combosQ = useApiQuery<{ items: Combination[] }>('/api/v1/coa/combinations?limit=100');
  const comboById = useMemo(() => new Map((combosQ.data?.items ?? []).map((c) => [c.id, c])), [combosQ.data]);

  const canDraft = persona?.roles.includes('MOF_TREASURY') ?? false;
  const canApprove = persona?.roles.includes('PS_FINANCE') ?? false;

  const act = async (w: Warrant, action: 'approve' | 'issue') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/warrants/${w.id}/${action}`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  if (!fy) return <ErrorNote message="No OPEN fiscal year." />;

  return (
    <div>
      <PageHeader
        title="Warrants & allotments"
        subtitle="Quarterly spending authority: Treasury drafts → PS Finance approves → issue creates allotments"
        actions={
          canDraft ? (
            <button className="btn-primary" onClick={() => setShowDraft(true)}>
              + Draft warrant
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="space-y-4">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <div className="card">
            <EmptyState title="No warrants" hint="Draft a quarterly warrant to release spending authority to ministries." />
          </div>
        ) : (
          listQ.data!.items.map((w) => (
            <div key={w.id} className="card p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{w.number}</span>
                    <StatusBadge status={w.status} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Q{w.quarter} · FY{fy.year} · drafted {formatDate(w.createdAt)}
                    {w.docRef ? ` · ${w.docRef}` : ''}
                    {w.approvedBy ? ` · approved by ${w.approvedBy}` : ''}
                  </div>
                </div>
                <div className="flex gap-2">
                  {canApprove && w.status === 'DRAFT' ? (
                    <button className="btn-success" onClick={() => act(w, 'approve')}>
                      Approve (PS Finance)
                    </button>
                  ) : null}
                  {canDraft && w.status === 'PS_FINANCE_APPROVED' ? (
                    <button className="btn-primary" onClick={() => act(w, 'issue')}>
                      Issue → create allotments
                    </button>
                  ) : null}
                </div>
              </div>
              <table className="mt-3 w-full">
                <thead>
                  <tr>
                    <th className="table-head">Budget line</th>
                    <th className="table-head text-right">Allotment</th>
                  </tr>
                </thead>
                <tbody>
                  {(w.lines ?? []).map((l) => {
                    const combo = comboById.get(l.coaCombinationId);
                    return (
                      <tr key={l.id}>
                        <td className="table-cell text-xs">{combo ? comboLabel(combo) : l.coaCombinationId}</td>
                        <td className="table-cell text-right tabular-nums">{scr(l.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      {showDraft ? (
        <DraftModal
          fyId={fy.id}
          combos={combosQ.data?.items ?? []}
          onClose={() => setShowDraft(false)}
          onDone={() => {
            setShowDraft(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DraftModal({
  fyId,
  combos,
  onClose,
  onDone,
}: {
  fyId: string;
  combos: Combination[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [quarter, setQuarter] = useState('3');
  const [docRef, setDocRef] = useState('');
  const [lines, setLines] = useState<Array<{ coaCombinationId: string; amount: string }>>([
    { coaCombinationId: '', amount: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/warrants/', {
        fiscalYearId: fyId,
        quarter: Number.parseInt(quarter, 10),
        docRef: docRef || undefined,
        lines: lines
          .filter((l) => l.coaCombinationId && l.amount)
          .map((l) => ({ coaCombinationId: l.coaCombinationId, amount: Number.parseFloat(l.amount) })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft warrant');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft quarterly warrant" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quarter">
            <select className="field-input" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Q{q}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Document reference (optional)">
            <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
          </Field>
        </div>
        <div>
          <div className="field-label">Warrant lines</div>
          {lines.map((row, idx) => (
            <div key={idx} className="mb-2 flex gap-2">
              <select
                className="field-input flex-1"
                value={row.coaCombinationId}
                onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, coaCombinationId: e.target.value } : r)))}
              >
                <option value="">Select budget line…</option>
                {combos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {comboLabel(c)}
                  </option>
                ))}
              </select>
              <input
                className="field-input w-40"
                type="number"
                min="0"
                step="0.01"
                placeholder="Amount"
                value={row.amount}
                onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
              />
              <button className="btn-secondary !px-2.5" onClick={() => setLines(lines.filter((_, i) => i !== idx))}>
                ✕
              </button>
            </div>
          ))}
          <button className="btn-secondary !py-1.5 text-xs" onClick={() => setLines([...lines, { coaCombinationId: '', amount: '' }])}>
            + Add line
          </button>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || lines.every((l) => !l.coaCombinationId || !l.amount)}
          >
            {busy ? 'Drafting…' : 'Draft warrant'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
